import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateTotp } from '../lib/totpGenerator';
import { playSound } from '../lib/soundEffects';

const { FiShield, FiX, FiCopy, FiCheck, FiKey, FiClock, FiLock } = FiIcons;

export default function TotpModal({
  isOpen,
  onClose,
  bookmark,
  onSaveSecret,
  themeColors: tc,
}) {
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('------');
  const [remaining, setRemaining] = useState(30);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (bookmark?.totpSecret) {
      setSecret(bookmark.totpSecret);
      setIsEditing(false);
    } else {
      setSecret('');
      setIsEditing(true);
    }
  }, [bookmark]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Rolling update timer
  useEffect(() => {
    if (!isOpen || !secret.trim()) return;

    let active = true;
    const updateCode = async () => {
      const res = await generateTotp(secret.trim());
      if (active) {
        setCode(res.code);
        setRemaining(res.secondsRemaining);
      }
    };

    updateCode();
    const interval = setInterval(updateCode, 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [isOpen, secret]);

  const handleCopy = () => {
    if (code === '------') return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    playSound('copy');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    playSound('lock');
    onSaveSecret?.(bookmark.id, secret.trim());
    setIsEditing(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative w-full max-w-sm rounded-2xl bg-[#121216] border border-[#26262e] shadow-2xl p-6 z-10 flex flex-col font-sans"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#24242c]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiShield} className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">2FA TOTP Authenticator</h3>
                <p className="text-[10px] text-[#777] font-mono truncate max-w-[180px]">
                  {bookmark?.title || 'Sovereign Token'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[#888] hover:text-white rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Main Display */}
          <div className="mt-6 flex flex-col items-center">
            {isEditing ? (
              <div className="w-full space-y-3">
                <label className="text-xs text-[#aaa] font-medium block">
                  Base32 2FA Secret Key:
                </label>
                <input
                  type="text"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="e.g. JBSWY3DPEHPK3PXP"
                  className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0d] border border-[#222] text-emerald-400 font-mono text-xs focus:outline-none focus:border-emerald-500/50"
                />
                <button
                  onClick={handleSave}
                  className="w-full py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs rounded-xl transition-all shadow-md shadow-emerald-500/20"
                >
                  Save Secret to Vault
                </button>
              </div>
            ) : (
              <>
                {/* 6-Digit Display */}
                <div
                  onClick={handleCopy}
                  className="cursor-pointer group flex flex-col items-center p-4 rounded-2xl bg-[#0a0a0d] border border-[#222] hover:border-emerald-500/40 transition-all w-full"
                >
                  <span className="text-3xl font-mono font-bold tracking-widest text-emerald-400 group-hover:scale-105 transition-transform">
                    {code.slice(0, 3)} {code.slice(3, 6)}
                  </span>

                  {/* Circular / Bar progress */}
                  <div className="w-full mt-3 flex items-center justify-between text-[11px] font-mono text-[#888]">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiClock} className="w-3 h-3 text-emerald-400" />
                      <span>{remaining}s left</span>
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy();
                      }}
                      className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3 h-3" />
                      <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>

                  <div className="w-full bg-[#1c1c24] h-1.5 rounded-full mt-2 overflow-hidden">
                    <div
                      className="bg-emerald-400 h-full transition-all duration-1000 ease-linear rounded-full"
                      style={{ width: `${(remaining / 30) * 100}%` }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-3 text-[11px] text-[#666] hover:text-[#999] transition-colors"
                >
                  Configure / Change Secret
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
